import _ from "lodash";
import type React from "react";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import { useCanvasStore } from "@/components/Canvas/stores/canvasStore";
import type { SimpleCanvasRef } from "@/components/SimpleCanvas/SimpleCanvas";
import { updateCanvasStoryboards } from "@/store/supabaseSlice";
import { useDarkModeStore } from "@/store/zustand/darkModeStore";
import { STORYBOARD_TYPES } from "@/utils/constants";
import { safeParseDocumentFromString } from "@/utils/safeHtmlParser/safeHtmlParser";
import {
  useActiveCanvas,
  useAutoUpdateRef,
  useCanvasId,
  usePrevious,
} from "@/utils/utils";
import { useIframePoolStore } from "../../IframePool/iframePoolStore";
import { getShellFromParsedHtml } from "../../IframePool/utils";
import { findElementByDomUniquePath } from "../../utils/utils";
import type { OutlineSystemRef } from "../OutlineSystem";
import { useApplyInstantUpdatesForStoryboard } from "./hooks/useApplyInstantUpdates";
import { useElementSelectHandler } from "./hooks/useElementSelectHandler";
import { useExtractCss } from "./hooks/useExtractCss";
import {
  applyDarkModeToHtml,
  detectInitialDarkMode,
} from "./utils/applyDarkModeToHtml";
import {
  changeHtmlToUseBaseUrl,
  FORCE_FULL_WIDTH_HEIGHT_CLASS_ID,
  FORCE_FULL_WIDTH_HEIGHT_CSS,
} from "./utils/domUtils";
import { morphBody } from "./utils/morphUtils";

interface IframeComponentProps {
  htmlContent: string;
  className?: string;
  style?: React.CSSProperties;
  baseUrl?: string;
  allowInteractions?: boolean;
  storyboardId?: string;
  hideOverflow?: boolean;
  storyboardType?: STORYBOARD_TYPES;
  outlineSystemRef: React.RefObject<OutlineSystemRef>;
  forceDragOver?: boolean;
  simpleCanvasRef?: React.RefObject<SimpleCanvasRef>;
  isResizing?: boolean;
  hideIframe?: boolean;
  resizeOnceRendered?: boolean;
}

const throttledUpdateHtmlShell = _.throttle(async (htmlContent: string) => {
  const doc = safeParseDocumentFromString(htmlContent);
  const newShell = getShellFromParsedHtml(doc);
  useIframePoolStore.getState().setLatestHtmlShell(newShell);
  const htmlAttributes = Array.from(doc.documentElement.attributes).map(
    (attr) => ({ name: attr.name, value: attr.value }),
  );
  return { htmlAttributes };
}, 1000);

const IframeComponent = forwardRef<
  {
    ref: HTMLIFrameElement;
  },
  IframeComponentProps
>(
  (
    {
      htmlContent,
      className,
      style,
      baseUrl,
      allowInteractions = false,
      storyboardId,
      hideOverflow = false,
      storyboardType,
      outlineSystemRef,
      forceDragOver = false,
      simpleCanvasRef,
      isResizing = false,
      hideIframe = false,
      resizeOnceRendered = false,
    },
    ref,
  ) => {
    const canvasId = useCanvasId();
    const activeCanvas = useActiveCanvas();
    const activeCanvasId = activeCanvas?.id;
    const activeCanvasIdRef = useRef(activeCanvasId);

    const iframeRef = useRef<HTMLIFrameElement>(null);
    const iframeDocRef = useRef<Document | null>(null);

    const navTree = useCanvasStore(
      (state) => state.storyboardToNavTree[storyboardId || ""],
    );

    const eventHandlerRef = useRef<HTMLDivElement | null>(null);
    const {
      setupElementSelection,
      isEditingText,
      isDragging,
      isDraggingAbsolute,
    } = useElementSelectHandler({
      storyboardId: storyboardId || "",
      allowInteractions,
      outlineSystemRef,
      forceDragOver,
      simpleCanvasRef,
      isResizing,
    });
    const previousHtmlContentRef = useRef<string | null>(null);
    const previousShellRef = useRef<string | null>(null);
    const previousBodyRef = useRef<string | null>(null);
    const darkMode = useDarkModeStore(
      (state) => state.canvasDarkModeStates[canvasId || ""],
    );

    const prevBaseUrl = usePrevious(baseUrl);

    const temporaryFiles = useCanvasStore((state) => state.temporaryFiles);

    const detectedDarkElementsRef = useRef<Element[]>([]);

    const everUpdatedIframeRef = useRef(false);

    const resizeOnceRenderedRef = useAutoUpdateRef(resizeOnceRendered);
    const resizedOnceRef = useRef(false);

    // Track blob URLs by file ID to avoid recreating them
    const blobUrlMapRef = useRef<Map<string, string>>(new Map());

    useApplyInstantUpdatesForStoryboard({
      storyboardId: storyboardId || "",
      iframeDocRef,
    });

    useExtractCss({
      storyboardId: storyboardId || "",
      iframeDocRef,
    });

    const scrollPositions = useIframePoolStore(
      (state) => state.storyboardScrollPositions[storyboardId || ""],
    );

    const isResizingRef = useAutoUpdateRef(isResizing);
    const isEditingTextRef = useAutoUpdateRef(isEditingText);
    const isDraggingRef = useAutoUpdateRef(isDragging);

    // Expose the iframe element to parent components
    useImperativeHandle(
      ref,
      () => ({
        ref: iframeRef.current!,
      }),
      [],
    );

    // Clean up blob URLs and stylesheet cache when component unmounts
    useEffect(() => {
      return () => {
        blobUrlMapRef.current.forEach((blobUrl) => {
          if (iframeRef.current?.contentWindow) {
            (iframeRef.current.contentWindow as any).URL.revokeObjectURL(
              blobUrl,
            );
          }
        });
        blobUrlMapRef.current.clear();
      };
    }, [storyboardId]);

    // Clean up contentWindow registration on unmount
    useEffect(() => {
      return () => {
        if (storyboardId) {
          useIframePoolStore
            .getState()
            .setContentWindowForStoryboard(storyboardId, null);
        }
      };
    }, [storyboardId]);

    const applyTemporaryFilesToIframe = useCallback(() => {
      if (!iframeRef.current?.contentWindow || !iframeDocRef.current) {
        return;
      }

      temporaryFiles.forEach((tempFile) => {
        // Check if we already have a blob URL for this file
        let blobUrl = blobUrlMapRef.current.get(tempFile.id);

        if (!blobUrl && iframeRef.current?.contentWindow) {
          // Create blob URL in the iframe's window context
          blobUrl = (
            iframeRef.current.contentWindow as any
          ).URL.createObjectURL(tempFile.file);
          blobUrlMapRef.current.set(tempFile.id, blobUrl || "");
        }

        // Find all images with this id and update their src
        if (blobUrl && iframeDocRef.current) {
          const img = iframeDocRef.current.getElementById(
            tempFile.id,
          ) as HTMLImageElement;
          if (img && img.src !== blobUrl) {
            img.src = blobUrl;
            img.style.cssText = "width:100%;height:100%;object-fit:contain;";
          }
        }
      });

      // Clean up blob URLs for files that no longer exist
      const currentFileIds = new Set(temporaryFiles.map((f) => f.id));
      blobUrlMapRef.current.forEach((blobUrl, id) => {
        if (!currentFileIds.has(id)) {
          (iframeRef.current?.contentWindow as any)?.URL.revokeObjectURL(
            blobUrl,
          );
          blobUrlMapRef.current.delete(id);
        }
      });
    }, [temporaryFiles]);

    const setScrollPosition = (
      domUniquePath: string,
      top: number,
      left: number,
    ) => {
      if (!iframeDocRef.current) {
        return;
      }

      if (domUniquePath === "body") {
        const element = iframeDocRef.current!.body;
        element.scrollTo(left, top);
        return;
      }

      if (domUniquePath === "html") {
        const element = iframeRef.current!.contentWindow;
        if (element) {
          element.scrollTo(left, top);
        }
        return;
      }

      const element = findElementByDomUniquePath(
        iframeDocRef.current!,
        domUniquePath,
      );
      if (element) {
        element.scrollTo(left, top);
      }
    };

    // Scroll position
    const prevScrollPositions = usePrevious(scrollPositions);
    useEffect(() => {
      if (!iframeDocRef.current) {
        return;
      }

      if (scrollPositions) {
        Object.keys(scrollPositions).forEach((domUniquePath) => {
          const prevScrollPosition: any = prevScrollPositions?.[domUniquePath];
          const scrollPosition = scrollPositions[domUniquePath];
          if (
            prevScrollPosition &&
            scrollPosition &&
            prevScrollPosition.left === scrollPosition.left &&
            prevScrollPosition.top === scrollPosition.top
          ) {
            return;
          }

          setScrollPosition(
            domUniquePath,
            scrollPosition.top,
            scrollPosition.left,
          );
        });
      }
    }, [scrollPositions, prevScrollPositions]);

    const processTopLevelDomElement = useCallback(() => {
      // Only do for non-application storyboards
      if (storyboardType === STORYBOARD_TYPES.APPLICATION) {
        return;
      }

      const navTreeForSb =
        useCanvasStore.getState().storyboardToNavTree[storyboardId || ""];

      if (navTreeForSb) {
        const element = findElementByDomUniquePath(
          iframeDocRef.current!,
          navTreeForSb.domUniquePath ?? navTreeForSb.domUniquePathForComponent,
        );

        if (
          resizeOnceRenderedRef.current &&
          !resizedOnceRef.current &&
          activeCanvasIdRef.current
        ) {
          const rect = element?.getBoundingClientRect();
          if (rect?.width && rect?.height) {
            resizedOnceRef.current = true;
            const storyboard = activeCanvas?.storyboards?.find(
              (sb) => sb.id === storyboardId,
            );
            updateCanvasStoryboards(activeCanvasIdRef.current, [
              {
                changeType: "update",
                storyboardId: storyboardId!,
                pageId: storyboard?.pageId || "",
                storyboardUpdateDict: {
                  width: rect.width,
                  height: rect.height,
                },
              },
            ]);
          }
        }

        // Also update the element to have w-full h-full using styles
        if (element) {
          element.classList.add(FORCE_FULL_WIDTH_HEIGHT_CLASS_ID);
        }
      }
    }, [
      activeCanvas?.storyboards,
      resizeOnceRenderedRef,
      storyboardId,
      storyboardType,
    ]);

    // Apply the full width height class to the top nav tree element when the nav tree changes
    useEffect(() => {
      processTopLevelDomElement();
    }, [processTopLevelDomElement, navTree]);

    useEffect(() => {
      // Only update the ifame html if one of the following has changed
      if (
        previousHtmlContentRef.current === htmlContent &&
        prevBaseUrl === baseUrl
      ) {
        return;
      }

      // If an element is being resized, don't update the contents so that the resize is not lost
      if (isResizingRef.current) {
        return;
      }

      // If is editing text, don't update the contents so that the text edit is not lost
      if (isEditingTextRef.current) {
        return;
      }

      // If is dragging an element, don't update the contents so that the drag is not lost
      if (isDraggingRef.current) {
        return;
      }

      const updateIframeContents = () => {
        // Reset detected dark elements when HTML content changes
        detectedDarkElementsRef.current = [];

        const iframe = iframeRef.current;
        const eventHandler = eventHandlerRef.current;
        if (iframe && eventHandler && htmlContent) {
          // Get the iframe's document
          const doc =
            iframe.contentDocument || (iframe.contentWindow as any).document;

          // Guard against race condition where document exists but isn't fully initialized
          if (!doc || !doc.documentElement) return;

          const scrollPositionsForSb =
            useIframePoolStore.getState().storyboardScrollPositions[
              storyboardId || ""
            ];

          const parseDocument = async () => {
            const shellHtml =
              useIframePoolStore.getState().shellForStoryboard[
                storyboardId || ""
              ];
            const bodyHtml =
              useIframePoolStore.getState().bodyHtmlForStoryboard[
                storyboardId || ""
              ];

            // Determine update strategy based on what changed
            const shellChanged = previousShellRef.current !== shellHtml;
            const bodyChanged = previousBodyRef.current !== bodyHtml;

            // Only do body-only update if shell hasn't changed, body has changed, and we've rendered before
            const bodyOnlyUpdate =
              !shellChanged &&
              bodyChanged &&
              everUpdatedIframeRef.current &&
              bodyHtml;

            // Based on initial testing the morphlex library seems to be faster for body only updates (5-15ms vs 10-20ms for innerHTML)
            // but for full document updates it is slower (70-100ms vs 40-60ms for innerHTML), so we are only using it for body only updates for now
            // Ideally morphlex also reduces flashing as it generally makes fewer mutations to the DOM
            let morphSuccess = false;
            if (bodyOnlyUpdate) {
              try {
                morphSuccess = morphBody(doc, doc.body, bodyHtml);
              } catch (error) {
                console.error(
                  "Morphing failed, falling back to innerHTML:",
                  error,
                );
              }
            }

            if (!morphSuccess) {
              // Fallback to innerHTML if morph fails
              if (bodyOnlyUpdate) {
                try {
                  doc.body.outerHTML = bodyHtml;
                } catch (error) {
                  console.error(
                    "Error updating iframe with innerHTML, fallback to full document update:",
                    error,
                  );
                  doc.documentElement.innerHTML = htmlContent;
                }
              } else {
                doc.documentElement.innerHTML = htmlContent;
              }
            }

            everUpdatedIframeRef.current = true;
            previousShellRef.current = shellHtml;
            previousBodyRef.current = bodyHtml;

            // Remove all script tags
            const scripts = doc.getElementsByTagName("script");
            while (scripts.length > 0) {
              scripts[0].parentNode.removeChild(scripts[0]);
            }

            if (hideOverflow && !scrollPositionsForSb?.["html"]) {
              doc.documentElement.style.overflow = "hidden";
            }

            // Make the background color transparent if this is not an application storyboard
            if (
              storyboardType &&
              storyboardType !== STORYBOARD_TYPES.APPLICATION
            ) {
              doc.documentElement.style.backgroundColor = "transparent";
              doc.documentElement.style.width = "100vw";
              doc.documentElement.style.height = "100vh";

              if (!scrollPositionsForSb?.["html"]) {
                doc.documentElement.style.overflow = "hidden";
              }

              doc.body.style.backgroundColor = "transparent";
              doc.body.style.width = "100vw";
              doc.body.style.height = "100vh";

              if (!scrollPositionsForSb?.["body"]) {
                doc.body.style.overflow = "hidden";
              }

              const root = doc.getElementById("root");
              if (root) {
                root.style.backgroundColor = "transparent";
                root.style.width = "100vw";
                root.style.height = "100vh";
                if (!scrollPositionsForSb?.["0"]) {
                  root.style.overflow = "hidden";
                }
              }

              const nextRoot = doc.getElementById("__next");
              if (nextRoot) {
                nextRoot.style.backgroundColor = "transparent";
                nextRoot.style.width = "100vw";
                nextRoot.style.height = "100vh";

                if (!scrollPositionsForSb?.["0"]) {
                  nextRoot.style.overflow = "hidden";
                }
              }
            }

            // Add base tag to resolve relative URLs
            if (baseUrl) {
              let baseTag = doc.querySelector("base");
              if (!baseTag) {
                baseTag = doc.createElement("base");
                // Insert at the beginning of head
                const head = doc.head;
                if (head.firstChild) {
                  head.insertBefore(baseTag, head.firstChild);
                } else {
                  head.appendChild(baseTag);
                }
              }
              // Ensure baseUrl has trailing slash for proper URL resolution
              const normalizedBaseUrl = baseUrl.endsWith("/")
                ? baseUrl
                : `${baseUrl}/`;
              baseTag.href = normalizedBaseUrl;

              changeHtmlToUseBaseUrl(doc, baseUrl);
            }

            const docAttributes = await throttledUpdateHtmlShell(htmlContent);
            docAttributes?.htmlAttributes.forEach((attr) => {
              doc.documentElement.setAttribute(attr.name, attr.value);
            });
          };
          parseDocument();

          iframeDocRef.current = doc;

          // Register contentWindow for instance lookup across all storyboards
          if (storyboardId && iframe.contentWindow) {
            useIframePoolStore
              .getState()
              .setContentWindowForStoryboard(
                storyboardId,
                iframe.contentWindow,
              );
          }

          // Update the previous HTML content reference after successful diff
          previousHtmlContentRef.current = htmlContent;

          // Get current values from store to avoid them as dependencies
          const currentDarkMode =
            useDarkModeStore.getState().canvasDarkModeStates[canvasId || ""];
          const currentDarkModeDetected =
            useDarkModeStore.getState().canvasDarkModeDetected[canvasId || ""];

          // Detect initial dark mode from the HTML on first load
          if (!currentDarkModeDetected && canvasId) {
            const darkModeDetection = detectInitialDarkMode(doc);
            detectedDarkElementsRef.current =
              darkModeDetection.elementsWithDarkClass;

            // Only set the initial dark mode if it hasn't been set yet for this canvas
            if (currentDarkMode === undefined) {
              if (darkModeDetection.isDarkMode) {
                // Set the canvas dark mode based on detected dark classes
                useDarkModeStore.getState().setCanvasDarkMode(canvasId, true);
              } else {
                // Set to false if no dark mode detected
                useDarkModeStore.getState().setCanvasDarkMode(canvasId, false);
              }
            }
            useDarkModeStore
              .getState()
              .setCanvasDarkModeDetected(canvasId, true);
          }

          // Get the new top nav tree element
          processTopLevelDomElement();

          // Add the full width height class to the document head
          const styleEl = doc.createElement("style");
          styleEl.textContent = FORCE_FULL_WIDTH_HEIGHT_CSS;
          (
            doc.documentElement.querySelector("head") || doc.documentElement
          ).appendChild(styleEl);

          // Always apply the current dark mode state (either from detection or from user toggle)
          applyDarkModeToHtml(
            doc,
            currentDarkMode !== undefined ? currentDarkMode : false,
            detectedDarkElementsRef.current,
          );
          // Apply temporary files after the content is loaded
          applyTemporaryFilesToIframe();

          // Set the scroll position
          if (scrollPositionsForSb) {
            Object.keys(scrollPositionsForSb).forEach((domUniquePath) => {
              const scrollPosition = scrollPositionsForSb[domUniquePath];
              setScrollPosition(
                domUniquePath,
                scrollPosition.top,
                scrollPosition.left,
              );
            });
          }

          // Add element selection functionality if enabled
          setupElementSelection(doc, eventHandler);
        }
      };

      // Call throttled update function
      updateIframeContents();
    }, [
      htmlContent,
      baseUrl,
      hideOverflow,
      setupElementSelection,
      storyboardType,
      applyTemporaryFilesToIframe,
      canvasId,
      prevBaseUrl,
      storyboardId,
      isResizingRef,
      isEditingTextRef,
      isDraggingRef,
      hideIframe,
      resizeOnceRenderedRef,
      processTopLevelDomElement,
    ]);

    // Apply temporary files when they change
    useEffect(() => {
      applyTemporaryFilesToIframe();
    }, [applyTemporaryFilesToIframe, temporaryFiles]);

    // Apply dark mode to the frozen html
    useEffect(() => {
      const doc =
        iframeRef.current?.contentWindow?.document ||
        iframeRef.current?.contentDocument;
      if (!doc) {
        return;
      }

      applyDarkModeToHtml(doc, darkMode, detectedDarkElementsRef.current);
    }, [darkMode]);

    const cleanUpElementSelectionFnRef = useRef<(() => void) | null>(null);

    return (
      <div
        ref={(el) => {
          if (el) {
            eventHandlerRef.current = el;
            if (iframeDocRef.current) {
              if (cleanUpElementSelectionFnRef.current) {
                cleanUpElementSelectionFnRef.current();
                cleanUpElementSelectionFnRef.current = null;
              }

              cleanUpElementSelectionFnRef.current = setupElementSelection(
                iframeDocRef.current,
                el,
              );
            }
          }
        }}
        style={{
          ...style,
          transform: "translateZ(0)", // Force GPU acceleration
          backfaceVisibility: "hidden",
          pointerEvents: allowInteractions ? "auto" : "none",
        }}
      >
        <iframe
          ref={iframeRef}
          className={className}
          style={{
            height: "100%",
            width: "100%",
            pointerEvents: "none",
            display: hideIframe ? "none" : "block",
          }}
          title="Iframe Content"
        />
      </div>
    );
  },
);

IframeComponent.displayName = "IframeComponent";

export default IframeComponent;
